import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../logo.png";
import Lottie from "lottie-react";
import backgroud from "../../assets/background.json";
import { X, Mic, Search, ChevronDown, MessageCircle  } from "lucide-react";
import "../../index.css";
import VoiceDetectionDialog from "../../components/VoiceDetectionDialog";
import SuggestionModal from "../../components/SuggestionModal";

const useAuth = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      const verifyToken = async () => {
        try {
          const response = await fetch(
            "https://kiosk-8nabk06g0-sergelens-projects.vercel.app/api/verify-token",
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            localStorage.removeItem("token");
            navigate("/");
          }
        } catch (error) {
          console.error("Token verification failed:", error);
          localStorage.removeItem("token");
          navigate("/");
        }
      };

      verifyToken();
    };

    checkAuth();
  }, [navigate]);
};

const Home = () => {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const wrapperRef = useRef(null);
  const [showVoiceDialog, setShowVoiceDialog] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const dropdownRef = useRef(null);

  // Add new useEffect for dropdown click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleVoiceResult = (transcript) => {
    console.log("Received transcript:", transcript);
    setSearchTerm(transcript);
  
    const findBestMatch = (transcript, options) => {
      const searchWord = transcript.toLowerCase().trim();
      
      // Create array of matches with their scores
      const matches = options.map(option => {
        const optionLower = option.toLowerCase();
        let score = 0;
        
        // Split the transcript into words and check each
        const searchWords = searchWord.split(/\s+/);
        searchWords.forEach(word => {
          // Handle different word endings
          const wordBase = word.replace(/[өү]х$/, ''); // Remove өх, үх endings
          const wordVariations = [
            word,
            wordBase + 'өх',
            wordBase + 'үх',
            wordBase + 'ох',
            word.replace(/[аэ]х$/, 'ох'), // Handle ах/эх -> ох variations
          ];
  
          // Check if any variation of the word is in the option
          if (wordVariations.some(variation => optionLower.includes(variation))) {
            score += 1;
          }
        });
  
        return {
          option,
          score
        };
      });
  
      // Sort by score and get the best match
      const bestMatches = matches.sort((a, b) => b.score - a.score);
      
      return bestMatches[0].score > 0 ? bestMatches[0].option : null;
    };
  
    // Get all possible matches
    const getAllMatches = (transcript, options) => {
      const searchWord = transcript.toLowerCase().trim();
      
      return options.filter(option => {
        const optionLower = option.toLowerCase();
        return searchWord.split(/\s+/).some(word => {
          const wordBase = word.replace(/[өү]х$/, '');
          const wordVariations = [
            word,
            wordBase + 'өх',
            wordBase + 'үх',
            wordBase + 'ох',
            word.replace(/[аэ]х$/, 'ох'),
          ];
          return wordVariations.some(variation => optionLower.includes(variation));
        });
      });
    };
  
    // Find the best matching option
    const bestMatch = findBestMatch(transcript, options);
    const allMatches = getAllMatches(transcript, options);
    
    console.log("Best match:", bestMatch);
    console.log("All matches:", allMatches);
  
    if (bestMatch) {
      // Navigate using the best match
      navigateToTemplate(bestMatch, transcript);
    } else if (allMatches.length > 0) {
      // If no best match but there are partial matches, use the first one
      navigateToTemplate(allMatches[0], transcript);
    } else {
      // If no matches found, you might want to show a message to the user
      console.log("No matching options found for:", transcript);
      // You could set some state here to show a message in the UI
      setSearchTerm(transcript); // Keep the transcript visible
    }
    
    setShowVoiceDialog(false);
  };

  useAuth();
  const menus = [
    { name: "Төлбөрийн даалгавар үүсгэх", link: "https://itax.mta.mn/modules/invoice/create-receipt", isExternal: true },
    { name: "Албадан гүйцэтгэх хүсэлтийн загвар", link: "#", isExternal: true },
    { name: "Улсын тэмдэгтийн хураамж тооцоолох", link: "https://dundgovi-court.mn/site/fees", isExternal: true },
    { name: "Шүүх хурлын зар, тойм", link: "https://live.shuukh.mn/#gsc.tab=0", isExternal: true },
    { name: "Шүүхийн шийдвэрийн цахим сан", link: "https://shuukh.mn/", isExternal: true },
    { name: "Тусгайлсан журмаар хэрэг хянан шийдвэрлэх ажиллагаа ", link: "https://www.supremecourt.mn/mn/home?page=pages&id=201&pr=200&tp=list&h=0", isExternal: true }
  ];

  // Handle menu item click
  const handleMenuClick = (menu) => {
    if (menu.isExternal) {
      window.open(menu.link, "_blank", "noopener,noreferrer");
    } else {
      navigate(menu.link);
    }
    setShowDropdown(false);
  };

  // Simple options array
  const options = [
    "Зээлийн гэрээний нэхэмжлэл",
    "Ажилд эргүүлэн тогтоолгох",
    "Эвлэрүүлэн зуучлалд өргөдөл гаргах",
    "Гэрлэлт цуцлуулж, хүүхдийн асрамж, тэтгэлэг гаргуулах хүсэлт",
    "Гэрээний үүрэг",
    "Хамтын амьдралтай байсныг тогтоолгох тухай",
    "Ажилласан жил тогтоолгох",
    "Гүйцэтгэх хуудас бичүүлэх хүсэлт",
    "Орон сууцны өмчлөгчөөр тогтоолгох тухай",
    "Сураггүй алга болсонд тооцуулах тухай",
    "Хүүхдийн тэтгэлэг гаргуулах",
    "Эрэн сурвалжлах",
    "Нас барсанд тооцуулах тухай",
    "Эрх зүйн чадамжгүйд тооцуулах тухай",
    "Эд хөрөнгө мөнгө гаргуулах тухай",
    "Эд хөрөнгөд учруулсан гэм хорын төлбөр гаргуулах тухай",
    "Нас тогтоолгох /төрсөн он, сар, өдөр зөвтгүүлэх тухай/",
    "Улсын бүртгэлд бүртгүүлэх тухай",
    "Нөхөн олговор гаргуулах тухай",
    "Бусдын хууль бус эзэмшлээс газар чөлөөлүүлэх тухай",
    "БУСАД ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА",
    "ЗЭЭЛИЙН ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА",
    "ХУДАЛДАХ-ХУДАЛДАН АВАХ ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА",
    "БУСДЫН ЭД ХӨРӨНГӨ, ЭРҮҮЛ МЭНДЭД ГЭМ ХОР УЧРУУЛСНЫ УЛМААС МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА",
    "БАНК, ЗЭЭЛИЙН ҮЙЛ АЖИЛЛАГАА ЭРХЛЭХ ЭРХ БҮХИЙ ЭТГЭЭДЭЭС ОЛГОСОН ЗЭЭЛИЙН ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА",
    "АЖИЛГҮЙ БАЙСАН ХУГАЦААНЫ ДУНДАЖ ЦАЛИН ХӨЛСТЭЙ ТЭНЦЭХ ОЛГОВОР ГАРГУУЛАХ ШААРДЛАГА",
    "НЭХЭМЖЛЭЛ",
  ];

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter options
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Input change handler
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowOptions(true);
  };

  function navigateToTemplate(selectedOption, transcript) {
    let section = "";
    switch (selectedOption) {
      case "Зээлийн гэрээний нэхэмжлэл":
        section = "зээлийн";
        break;
      case "Ажилд эргүүлэн тогтоолгох":
        section = "ажилд";
        break;
      case "Эвлэрүүлэн зуучлалд өргөдөл гаргах":
        section = "эвлэрүүлэн";
        break;
      case "Гэрлэлт цуцлуулж, хүүхдийн асрамж, тэтгэлэг гаргуулах хүсэлт":
        section = "гэрлэлт";
        break;
      case "Гэрээний үүрэг":
        section = "үүрэг";
        break;
      case "Хамтын амьдралтай байсныг тогтоолгох тухай":
        section = "хамтын";
        break;
      case "Ажилласан жил тогтоолгох":
        section = "ажилласанЖил";
        break;
      case "Гүйцэтгэх хуудас бичүүлэх хүсэлт":
        section = "гүйцэтгэх";
        break;
      case "Орон сууцны өмчлөгчөөр тогтоолгох тухай":
        section = "оронСууц";
        break;
      case "Сураггүй алга болсонд тооцуулах тухай":
        section = "сураггүй";
        break;
      case "Хүүхдийн тэтгэлэг гаргуулах":
        section = "тэтгэлэг";
        break;
      case "Эрэн сурвалжлах":
        section = "эрэнСурвалжлах";
        break;
      case "Нас барсанд тооцуулах тухай":
        section = "насБарсан";
        break;
      case "Эрх зүйн чадамжгүйд тооцуулах тухай":
        section = "эрхЗүй";
        break;
      case "Эд хөрөнгө мөнгө гаргуулах тухай":
        section = "эдХөрөнгө";
        break;
      case "Эд хөрөнгөд учруулсан гэм хорын төлбөр гаргуулах тухай":
        section = "эдХөрөнгөдУчруулсанГэмХор";
        break;
      case "Нас тогтоолгох /төрсөн он, сар, өдөр зөвтгүүлэх тухай/":
        section = "насТогтоолгох";
        break;
      case "Улсын бүртгэлд бүртгүүлэх тухай":
        section = "улсынБүртгэл";
        break;
      case "Нөхөн олговор гаргуулах тухай":
        section = "нөхөнОлговор";
        break;
      case "Бусдын хууль бус эзэмшлээс газар чөлөөлүүлэх тухай":
        section = "бусдынХууль";
        break;
      case "БУСАД ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА":
        section = "журам";
        break;
      case "ЗЭЭЛИЙН ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА":
        section = "журам";
        break;
      case "ХУДАЛДАХ-ХУДАЛДАН АВАХ ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА":
        section = "журам";
        break;
      case "БУСДЫН ЭД ХӨРӨНГӨ, ЭРҮҮЛ МЭНДЭД ГЭМ ХОР УЧРУУЛСНЫ УЛМААС МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА":
        section = "журам";
        break;
      case "БАНК, ЗЭЭЛИЙН ҮЙЛ АЖИЛЛАГАА ЭРХЛЭХ ЭРХ БҮХИЙ ЭТГЭЭДЭЭС ОЛГОСОН ЗЭЭЛИЙН ГЭРЭЭНИЙ ДАГУУ МӨНГӨН ХӨРӨНГӨ ГАРГУУЛАХ ШААРДЛАГА":
        section = "журам";
        break;
      case "АЖИЛГҮЙ БАЙСАН ХУГАЦААНЫ ДУНДАЖ ЦАЛИН ХӨЛСТЭЙ ТЭНЦЭХ ОЛГОВОР ГАРГУУЛАХ ШААРДЛАГА":
        section = "журам";
        break;
      case "НЭХЭМЖЛЭЛ":
        section = "журам";
        break;
      default:
        section = "зээлийн";
    }
    console.log(selectedOption);
    console.log(section);
    navigate("/documenttemplate", {
      state: {
        selectedOption: selectedOption,
        selectedSection: section,
        userResponse: transcript
      },
    });
  }

  // Clear handler
  const handleClear = () => {
    setSearchTerm("");
    setShowOptions(false);
  };

    return (
    <div
      className="min-h-screen bg-gradient-to-br via-black to-black relative overflow-hidden"
      style={{ backgroundColor: "#f0f4f9" }}
    >
      <div className="absolute inset-0 opacity-50" />
      <div className="relative z-10">
        <header
          className="bg-blue-600 text-white"
          style={{ backgroundColor: "#21396c" }}
        >
          <div className="container mx-auto px-4">
            <nav className="flex justify-between py-3">
              <div className="flex items-center space-x-4">
                <img
                  src={logo}
                  alt="DAN System Logo"
                  className="mb-4"
                  style={{ width: "200px", height: "50px" }}
                />
              </div>
              <div className="flex items-center space-x-6">
                <div className="relative" ref={dropdownRef}>
                  <button
                    className="flex items-center space-x-1 px-4 py-2 rounded-lg bg-blue-700 transition-colors"
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    <span>Үйлчилгээнүүд</span>
                    <ChevronDown size={16} className={`transform transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg py-2 z-50">
              {menus.map((menu, index) => (
                <button
                  key={index}
                  className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    handleMenuClick(menu)
                  }}
                >
                  {menu.name}
                </button>
              ))}
                    </div>
                  )}
                </div>
                
                {/* Санал хүсэлт Button */}
                <button
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 transition-colors"
                  onClick={() => setShowSuggestionModal(true)}
                >
                  <MessageCircle size={16} />
                  <span>Санал хүсэлт</span>
                </button>
              </div>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 text-center mt-20">
          {/* <div className=" mb-4">🔉 VOICE ASSISTANT</div> */}

          

          <h1 className="text-4xl font-bold mb-6 flex flex-col items-center gap-6">
            <span
              className="font-bold"
              style={{
                background: "linear-gradient(90deg, #3B82F6 0%, #00BFFF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Танд ямар үйлчилгээ хэрэгтэй вэ?
            </span>
            {/* Decorative Microphone */}
          <div className="mt-10 relative "onClick={() => setShowVoiceDialog(true)}>
            <div
              className="w-20 h-20 mx-auto bg-gradient-to-r from-[#3B82F6] to-[#284687] rounded-full flex items-center justify-center cursor-pointer" // Added cursor-pointer
               // Move click handler to the container
            >
              <Mic size={40} className="text-white" />
            </div>
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full opacity-20 animate-pulse"
              style={{ backgroundColor: "#3B82F6" }}
            />
          </div>

          {/* Move dialog render outside the decorative microphone div */}
          {showVoiceDialog && (
            <VoiceDetectionDialog
              setShowVoiceDialog={setShowVoiceDialog}
              onResult={handleVoiceResult}
            />
          )}
            <button
              className="text-xl px-8 py-4 m-10 rounded-full hover:opacity-90 transition-colors flex items-center gap-2 shadow-lg"
              style={{
                background: "linear-gradient(90deg, #3B82F6 0%, #00BFFF 100%)",
              }}
              onClick={() => setShowSearch(true)}
            >
              <span className="text-white">Текстээр хайх</span>
              <span className="text-white text-2xl">→</span>
            </button>
          </h1>

          {/* Search Input (Conditionally Rendered) */}
          {showSearch && (
            <div className="max-w-2xl mx-auto mt-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Үйлчилгээ хайх..."
                  value={searchTerm}
                  onChange={handleInputChange}
                  style={{ backgroundColor: "white", color: "#21396c" }}
                  className="w-full px-4 py-3 rounded-full border border-gray-600 bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-black-500"
                />
                {searchTerm && (
                  <button
                    onClick={handleClear}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-white-400 hover:white-gray-600 p-1"
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
                <button
                  onClick={() => setIsListening(!isListening)}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all duration-200`}
                >
                  <Search size={20} />
                </button>
              </div>

              {showOptions && filteredOptions.length > 0 && (
                <ul className="mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredOptions.map((option, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-left text-white"
                      style={{ backgroundColor: "white", color: "#21396c" }}
                      onClick={() => {
                        setSearchTerm(option);
                        setShowOptions(false);
                        navigateToTemplate(option);
                      }}
                    >
                      {option}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          
        </main>

        {/* Suggestion Modal */}
        {showSuggestionModal && (
          <SuggestionModal onClose={() => setShowSuggestionModal(false)} />
        )}
      </div>
    </div>
  );
};

export default Home;